import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Card from '../../../components/ui/Card';
import Icon from '../../../components/AppIcon';

const RevenueChart = ({ data = [], aiAnalysis = null, loading = false }) => {
  const getTrendColor = (trend) => {
    switch (trend) {
      case 'increasing':
        return 'text-success-600';
      case 'decreasing':
        return 'text-danger-600';
      default:
        return 'text-muted-foreground';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing':
        return 'TrendingUp';
      case 'decreasing':
        return 'TrendingDown';
      default:
        return 'Minus';
    }
  };

  const getTrendBgColor = (trend) => {
    switch (trend) {
      case 'increasing':
        return 'bg-success-50 border-success-200';
      case 'decreasing':
        return 'bg-danger-50 border-danger-200';
      default:
        return 'bg-muted border-border';
    }
  };

  return (
    <div className="space-y-6">
      {/* AI Revenue Analysis */}
      {aiAnalysis && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Icon name="Brain" className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">OpenAI Revenue Analysis</h3>
              <p className="text-sm text-muted-foreground">GPT-5 Financial Insights</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className={`p-4 rounded-lg border ${getTrendBgColor(aiAnalysis?.trend)}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Growth Trend</span>
                <div className="flex items-center gap-2">
                  <Icon name={getTrendIcon(aiAnalysis?.trend)} className={`w-5 h-5 ${getTrendColor(aiAnalysis?.trend)}`} />
                  <span className={`text-xl font-bold ${getTrendColor(aiAnalysis?.trend)}`}>
                    {aiAnalysis?.growthRate > 0 ? '+' : ''}{aiAnalysis?.growthRate?.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg border bg-primary-50 border-primary-200">
              <div className="text-sm font-medium text-foreground mb-1">Quarterly Forecast</div>
              <div className="text-xl font-bold text-primary-600">
                ${aiAnalysis?.quarterlyForecast?.toLocaleString()}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Icon name="Lightbulb" className="w-4 h-4" />
              AI Insights
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {aiAnalysis?.insights}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="AlertTriangle" className="w-4 h-4 text-warning-600" />
                Risk Factors
              </h4>
              <ul className="space-y-1.5">
                {aiAnalysis?.riskFactors?.map((risk, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                    <Icon name="Minus" className="w-4 h-4 text-danger-600 mt-0.5 flex-shrink-0" />
                    <span>{risk}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="TrendingUp" className="w-4 h-4 text-success-600" />
                Growth Opportunities
              </h4>
              <ul className="space-y-1.5">
                {aiAnalysis?.opportunities?.map((opp, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                    <Icon name="Plus" className="w-4 h-4 text-success-600 mt-0.5 flex-shrink-0" />
                    <span>{opp}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {loading && !aiAnalysis && (
        <Card className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Analyzing revenue with OpenAI...</p>
            </div>
          </div>
        </Card>
      )}

      {/* Revenue Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Revenue by Center</h3>
        {data?.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="centerName" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#3b82f6" name="Revenue ($)" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="DollarSign" className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No revenue data available</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default RevenueChart;